import { Deal, DealTransformed } from "@/lib/schema/deal-schema";
import { GICSSubIndustries, GICSIndustries, getStageText } from "@/lib/utils";

// Build the unified `tearsheets_all` list the panel toolbar and reports-tab
// dropdowns read from. V1 (legacy) tearsheets are AI-team-only and must not
// surface in the user-facing dropdown — historical V1 entries are kept in
// `aiGenerations.tearsheets` for any backend consumer that needs them.
export const buildTearsheetsAll = (
  tearsheetsV2: DealTransformed["aiGenerations"]["tearsheets_v2"] | undefined,
): DealTransformed["aiGenerations"]["tearsheets_all"] =>
  (tearsheetsV2 || [])
    .map((t) => ({
      _id: t._id,
      created_on: t.created_on,
      kind: "v2" as const,
      data: t.data,
    }))
    .sort(
      (a, b) =>
        new Date(b.created_on).getTime() - new Date(a.created_on).getTime(),
    );

export const transformDealData = (deal: Deal): DealTransformed => {
  // Debug logging for ai_tracked transformation
  console.log("=== transformDealData ===");
  console.log("deal.ai_tracked:", deal.ai_tracked);
  console.log("deal.ai_tracked type:", typeof deal.ai_tracked);
  console.log("deal.ai_tracked === true:", deal.ai_tracked === true);
  console.log("deal.ai_tracked === false:", deal.ai_tracked === false);

  // Debug logging for v2 data sufficiency
  if (deal.aiGenerations?.datasufficiencyscores_v2?.length > 0) {
    console.log(
      "Found v2 data sufficiency reports:",
      deal.aiGenerations.datasufficiencyscores_v2
    );
  }

  // Get industry: walk the candidate deal_info fields in order of specificity
  // (level-5 sub-industries → parent industries → legacy `industry` /
  // `Industries`), resolving GICS codes through the level-5 map first and
  // falling back to the parent-industry map. Only approved sub-industry
  // entries promote into overview; the other fields propagate as-is so
  // pending parent codes still render through the existing UI fallbacks.
  let industry: string[] = [];
  const resolveCode = (code: unknown): string => {
    const s = String(code).trim();
    if (!s) return s;
    const sub = GICSSubIndustries.find((si) => si.code === s);
    if (sub) return sub.name;
    const parent = GICSIndustries.find((i) => i.code === s);
    if (parent) return parent.name;
    return s;
  };
  const subIndustriesField = deal.deal_info?.find(
    (info) =>
      info.field_name === "sub_industries" && info.field_state === "approved",
  );
  if (subIndustriesField?.field_value) {
    const fieldValue = subIndustriesField.field_value;
    const codes = Array.isArray(fieldValue) ? fieldValue : [fieldValue as string];
    const tagNames = codes
      .map((code) => GICSSubIndustries.find((si) => si.code === String(code))?.name)
      .filter((name): name is string => !!name);
    if (tagNames.length > 0) {
      industry = tagNames;
    }
  }
  if (industry.length === 0) {
    const industryFromDealInfo = deal.deal_info?.find((info) =>
      ["industries", "industry", "Industries"].includes(info.field_name),
    );
    if (industryFromDealInfo?.field_value) {
      const raw = industryFromDealInfo.field_value;
      if (Array.isArray(raw)) {
        industry = raw.map(resolveCode);
      } else if (typeof raw === "string" || typeof raw === "number") {
        industry = [resolveCode(raw)];
      }
    } else if (Array.isArray(deal.industry) && deal.industry.length > 0) {
      industry = deal.industry;
    } else if (deal.industry && typeof deal.industry === "string") {
      industry = [deal.industry];
    }
  }

  return {
    id: deal.applicant_company,
    /**
     * The two endpoints that feed this transform disagree about `_id`.
     *
     * `fetch_simple_deal_list` returns applications, so `_id` is the
     * application. `fetch_single_deal` overrides `_id` with the *company* and
     * sends the application as `application_id`. Reading `_id` alone on the deal
     * page produced a valid ObjectId for the wrong document, which fails as a
     * silent "not found".
     */
    applicationId: String(deal.application_id ?? deal._id),
    overview: {
      name: deal.company_name,
      status: deal.application_status,
      risk: deal.aiGenerations.startupscores?.[0]?.startupscore?.Overall?.Score,
      industry,
      addedOn: deal.created_on,
      website: deal.company_website,
      country: deal.country ?? null,
      location: deal.address,
      description: deal.short_bio,
      businessModel: Array.isArray(deal.business_model)
        ? deal.business_model.filter(Boolean).join(", ") || null
        : deal.business_model ?? null,
      companyStage: getStageText(deal.stage),
      founded: deal.founded_year,
      websiteSummary: deal.website_summary || {
        website_summary: "",
        created_on: "",
      },

      source: deal.source || deal.company_source || "",
      companySource: deal.company_source || "",
    },
    dealNotes: deal.deal_notes || [],
    dealInfo: {
      deal: deal.deal_info,
      applicationInfo: deal.applicationInfo,
    },
    questionnaire: deal.questionnaire || [],
    aiTracked: (() => {
      const result = deal.ai_tracked === true;
      console.log("Transforming ai_tracked:", deal.ai_tracked, "->", result);
      return result;
    })(),
    // Absent on every deal created before progressive diligence shipped, and
    // those all ran fully — so missing means "full", not "unknown".
    diligenceLevel:
      (deal as { diligence_level?: "light" | "full" }).diligence_level ===
      "light"
        ? "light"
        : "full",
    websiteTrafficGrowth: deal.website_traffic_growth ?? null,
    aiColumnValues: (deal as { ai_column_values?: Record<string, unknown> })
      .ai_column_values ?? {},
    viewIds: ((deal as { view_ids?: unknown[] }).view_ids ?? []).map(String),
    raiseBoard: deal.raise_board === true,
    globalVisibility: deal.global_visibility === true,
    aiGenerations: {
      datasufficiency: deal.aiGenerations.datasufficiencyscores || [],
      datasufficiency_v2: deal.aiGenerations.datasufficiencyscores_v2 || [],
      summaries: deal.aiGenerations.summaries || [],
      tearsheets: deal.aiGenerations.tearsheets || [],
      tearsheets_v2: deal.aiGenerations.tearsheets_v2 || [],
      tearsheets_all: buildTearsheetsAll(deal.aiGenerations.tearsheets_v2),
      startupscores: deal.aiGenerations.startupscores || [],
      market_intel: deal.aiGenerations.market_intel || [],
      metrics: deal.aiGenerations.metrics || [],
      valuations:
        deal.aiGenerations.valuations?.map((valuation) => ({
          _id: valuation._id?.toString(),
          valuationLow: valuation.valuationLow || 0,
          valuationMid: valuation.valuationMid || 0,
          valuationHigh: valuation.valuationHigh || 0,
          cashRaised: valuation.cashRaised || 0,
          dilution: valuation.dilution || 0,
          valuationMethod:
            valuation.valuationMethod || "Weighted Score Analysis",
          startupScore: valuation.startupScore || {},
          dataSufficiencyScore: valuation.dataSufficiencyScore || {},
          stage: valuation.stage || "1",
          created_on: valuation.created_on || new Date().toISOString(),
        })) || [],
      chats: deal.aiGenerations.chats || [],
      website_summary_v2: deal.aiGenerations.website_summary_v2 || [],
      market_research: deal.aiGenerations.market_research || [],
      linkedInInfo:
        (
          deal.aiGenerations as Deal["aiGenerations"] & {
            linkedInInfo?: Array<{
              _id: string;
              data: unknown;
              created_on: string;
            }>;
          }
        ).linkedInInfo || [],
      gap_analysis: deal.aiGenerations.gap_analysis || [],
      deal_fit: deal.aiGenerations.deal_fit || [],
      insights: deal.aiGenerations.insights || [],
    },
    company_files: deal.company_files,
    reviews: deal.reviews || [],
    content: deal.content || { extracted: {}, anonymized: {} },
    fitScore: deal.fit_score || {
      score: 0,
      breakdown: {
        distinction: { score: 0, matches: [], target: [] },
        industry: { score: 0, matches: [], target: [] },
        country: { score: 0, matches: [], target: [] },
        stage: { score: 0, matches: [], target: [] },
        businessModel: { score: 0, matches: [], target: [] },
      },
    },
    prospectiveAffEmail: deal.prospective_aff_email || "",
    reportStatus: {
      opportunityApplication: {
        report_status: deal.report_status,
      },
    },
  };
};

export const transformDeals = (deals: Deal[]) =>
  deals.reduce((acc, deal) => {
    try {
      if (!deal.applicant_company || !deal.company_name) {
        return acc;
      }

      const transformed = transformDealData(deal);
      return {
        ...acc,
        [deal.applicant_company]: transformed,
      };
    } catch (error) {
      console.error("Error transforming deal:", deal, error);
      return acc;
    }
  }, {} as Record<string, DealTransformed>);

export const getLastActivityDate = (deal: DealTransformed): Date => {
  const dates = [
    new Date(deal.overview.addedOn),
    ...deal.company_files.map((file) => new Date(file.time_stamp)),
    ...deal.aiGenerations.datasufficiency.map((ds) => new Date(ds.created_on)),
    ...deal.aiGenerations.datasufficiency_v2.map(
      (ds) => new Date(ds.created_on)
    ),
    ...deal.aiGenerations.summaries.map((s) => new Date(s.created_on)),
    ...deal.aiGenerations.tearsheets.map((ts) => new Date(ts.created_on)),
    ...deal.aiGenerations.startupscores.map((ss) => new Date(ss.created_on)),
    ...deal.aiGenerations.metrics.map((m) => new Date(m.created_on)),
    ...deal.aiGenerations.valuations.map((v) => new Date(v.created_on)),
    ...deal.aiGenerations.chats.map((c) => new Date(c.created_on)),
    ...(deal.aiGenerations.insights || []).map((i) => new Date(i.created_on)),
    ...deal.dealNotes.map((note) => new Date(note.created_on)),
    ...deal.reviews.map((review) => new Date(review.created_on)),
  ];

  // Filter out invalid dates
  const validDates = dates.filter((date) => !isNaN(date.getTime()));

  // If no valid dates found, return current date as fallback
  if (validDates.length === 0) {
    return new Date();
  }

  return new Date(Math.max(...validDates.map((date) => date.getTime())));
};
